import { useMemo, useCallback } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { NodeType } from '@/enums/node-type.enum';
import { FF_NODE_GROUPING } from '@/consts/featureFlags';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { useGroupContextMenu } from './useGroupContextMenu';
import { useMultiSelectionContextMenu } from './useMultiSelectionContextMenu';
import { useFloatMenu } from './useFloatMenu';
import { useStickyNoteContextMenu } from './useStickyNoteContextMenu';
import type { Node } from '@/types/node';
import type { AddNewNodeOptions } from '../flowTypes';

interface UseContextMenusProps {
  addNewNode: ({ action, dropX, dropY, connection }: AddNewNodeOptions) => void;
}

export const useContextMenus = ({ addNewNode }: UseContextMenusProps) => {
  const isNodeGroupingEnabled = useFeatureFlagEnabled(FF_NODE_GROUPING);
  const role = useUserWorkflowRole();
  const { openGroupContextMenu, closeGroupContextMenu, groupContextMenuComponent } = useGroupContextMenu({
    addNewNode,
  });
  const { openMultiSelectionContextMenu, closeMultiSelectionContextMenu, multiSelectionContextMenuComponent } =
    useMultiSelectionContextMenu();
  const { openStickyNoteContextMenu, closeStickyNoteContextMenu, stickyNoteContextMenuComponent } =
    useStickyNoteContextMenu();

  const { floatMenu, suggestionMenu, floatMenuComponent } = useFloatMenu({
    addNewNode,
    openGroupContextMenu,
  });
  const { handleFloatMenuClose } = floatMenu;

  const nodeContextMenuComponents = useMemo(
    () =>
      [groupContextMenuComponent, multiSelectionContextMenuComponent, stickyNoteContextMenuComponent].filter(
        (component): component is React.JSX.Element => Boolean(component),
      ),
    [groupContextMenuComponent, multiSelectionContextMenuComponent, stickyNoteContextMenuComponent],
  );

  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, node: Node) => {
      handleFloatMenuClose();
      closeMultiSelectionContextMenu();

      if (node.type === NodeType.CustomGroup) {
        event.preventDefault();
        closeStickyNoteContextMenu();
        openGroupContextMenu(event.clientX, event.clientY, node.id);
      }
      if (node.type === NodeType.StickyNote) {
        event.preventDefault();
        closeGroupContextMenu();
        openStickyNoteContextMenu(event.clientX, event.clientY, node.id);
      }
    },
    [
      handleFloatMenuClose,
      closeMultiSelectionContextMenu,
      openGroupContextMenu,
      closeStickyNoteContextMenu,
      openStickyNoteContextMenu,
      closeGroupContextMenu,
    ],
  );

  const handleSelectionContextMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // Only show menu if node grouping is enabled and user is not a guest
      if (!isNodeGroupingEnabled || role === 'guest') {
        return;
      }

      handleFloatMenuClose();
      closeGroupContextMenu();
      closeStickyNoteContextMenu();
      event.preventDefault();

      openMultiSelectionContextMenu(event.clientX, event.clientY);
    },
    [
      handleFloatMenuClose,
      isNodeGroupingEnabled,
      role,
      openMultiSelectionContextMenu,
      closeGroupContextMenu,
      closeStickyNoteContextMenu,
    ],
  );

  return {
    handleNodeContextMenu,
    handleSelectionContextMenu,
    floatMenu,
    suggestionMenu,
    menuComponents: [...(floatMenuComponent ? [floatMenuComponent] : []), ...nodeContextMenuComponents],
    isNodeMenuOpened: nodeContextMenuComponents.length > 0,
  };
};
